/**
 * VGCASignService WebSocket API Wrapper
 * ======================================
 * Giao tiếp với VGCASignService (localhost) để ký số bằng USB Token.
 *
 * Hỗ trợ 2 chế độ:
 *   - Ký pháp lý: Sử dụng USB Token từ CA thương mại (VNPT-CA, Viettel-CA, FPT-CA, ...)
 *   - Ký Ban cơ yếu: Sử dụng USB Token từ VGCA (Ban Cơ yếu Chính phủ)
 *
 * Cả 2 chế độ đều giao tiếp qua cùng WebSocket API, chỉ khác loại chứng thư số trên Token.
 *
 * Cổng mặc định: 8987 (WSS)
 * Base URL: wss://127.0.0.1:8987
 */

const VGCA_PORT = 8987
const VGCA_BASE_URL = `wss://127.0.0.1:${VGCA_PORT}`
const WS_TIMEOUT = 120000 // 2 phút - chờ người dùng nhập PIN

function sendWebSocket(endpoint, payload, timeout = WS_TIMEOUT) {
  return new Promise((resolve, reject) => {
    const url = `${VGCA_BASE_URL}${endpoint}`
    let ws
    let timer

    try {
      ws = new WebSocket(url)
    } catch (err) {
      reject(new Error(
        `Không thể kết nối VGCASignService tại ${url}. ` +
        'Vui lòng kiểm tra ứng dụng đã được cài đặt và đang chạy.'
      ))
      return
    }

    timer = setTimeout(() => {
      ws.close()
      reject(new Error('Hết thời gian chờ phản hồi từ VGCASignService.'))
    }, timeout)

    ws.onopen = () => {
      ws.send(JSON.stringify(payload))
    }

    ws.onmessage = (event) => {
      clearTimeout(timer)
      try {
        const data = JSON.parse(event.data)
        resolve(data)
      } catch (err) {
        reject(new Error('Phản hồi từ VGCASignService không hợp lệ.'))
      }
      ws.close()
    }

    ws.onerror = () => {
      clearTimeout(timer)
      reject(new Error(
        'Lỗi kết nối VGCASignService. ' +
        'Vui lòng kiểm tra: (1) Ứng dụng đang chạy, (2) USB Token đã cắm.'
      ))
    }

    ws.onclose = (event) => {
      clearTimeout(timer)
      if (!event.wasClean && event.code !== 1000) {
        reject(new Error('Kết nối WebSocket bị đóng bất thường.'))
      }
    }
  })
}

/**
 * Kiểm tra VGCASignService có đang chạy không
 */
export async function checkAvailability() {
  return new Promise((resolve) => {
    const url = `${VGCA_BASE_URL}/GetCertInfo`
    let ws

    try {
      ws = new WebSocket(url)
    } catch {
      resolve(false)
      return
    }

    const timer = setTimeout(() => {
      ws.close()
      resolve(false)
    }, 5000)

    ws.onopen = () => {
      clearTimeout(timer)
      ws.close()
      resolve(true)
    }

    ws.onerror = () => {
      clearTimeout(timer)
      resolve(false)
    }
  })
}

/**
 * Lấy thông tin chứng thư số từ USB Token
 */
export async function getCertificates() {
  const result = await sendWebSocket('/GetCertInfo', {
    command: 'list'
  })

  if (result.Status !== 0) {
    throw new Error(result.Message || 'Không lấy được thông tin chứng thư số.')
  }

  return result
}

/**
 * Ký hash bằng USB Token
 * @param {string} hashBase64 - Hash (Base64) cần ký, nhận từ Backend
 */
export async function signHash(hashBase64) {
  const payload = {
    HashValue: hashBase64,
    HashAlg: 'SHA-256',
    Encode: 'UTF-8',
    SignerInfo: ''
  }

  const result = await sendWebSocket('/SignHash', payload)

  if (result.Status !== 0) {
    throw new Error(result.Message || 'Ký hash thất bại.')
  }

  return result
}

/**
 * Trích xuất tên người ký từ Subject của chứng thư số
 */
export function parseCertSubject(subject) {
  if (!subject) return ''
  const match = subject.match(/CN=([^,]+)/)
  return match ? match[1].trim() : subject
}

export default {
  checkAvailability,
  getCertificates,
  signHash,
  parseCertSubject
}
